<template>
  <div v-redirect-if-no-permission:VIEW_FINANCE_REPORTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-card>
      <div class="fin-header fin-header--sticky fr-no-print">
        <div class="fin-toolbar">
          <span class="fin-title">{{ t("title") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!detailed" @click="expandAll">
              <v-icon small>unfold_more</v-icon>
            </v-btn>
            <span>{{ c("expandAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="collapseAll">
              <v-icon small>unfold_less</v-icon>
            </v-btn>
            <span>{{ c("collapseAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="exportExcel">
              <v-icon small>grid_on</v-icon>
            </v-btn>
            <span>{{ c("exportExcel") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="downloadCsv">
              <v-icon small>file_download</v-icon>
            </v-btn>
            <span>{{ c("exportCsv") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="exportPrint">
              <v-icon small>print</v-icon>
            </v-btn>
            <span>{{ c("print") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadReport">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ c("refresh") }}</span>
          </v-tooltip>
        </div>
        <div class="fin-filters">
          <v-text-field v-model="filters.from" type="date" :label="c('from')" hide-details class="fin-field"></v-text-field>
          <v-text-field v-model="filters.to" type="date" :label="c('to')" hide-details class="fin-field"></v-text-field>
          <v-select
            v-model="filters.currency"
            :items="currencyOptions"
            item-text="text"
            item-value="value"
            :label="t('currencyFilter')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-select
            v-model="filters.level"
            :items="levelOptions"
            item-text="text"
            item-value="value"
            :label="t('level')"
            hide-details
            class="fin-field fin-field--wide"
          ></v-select>
          <v-checkbox
            v-model="filters.roll_up_parties"
            :label="t('rollUp')"
            hide-details
            class="fin-field gjs-checkbox"
          ></v-checkbox>
          <v-btn small color="primary" @click="loadReport">{{ c("apply") }}</v-btn>
        </div>
      </div>

      <div v-for="(warning, index) in warnings" :key="'w' + index" class="fin-alert-error fr-no-print">
        <v-icon small color="#c62828">warning</v-icon>
        {{ warning.text }}
      </div>

      <div v-if="hasReport" class="fin-summary fr-no-print">
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("totalDebit") }}</span>
          <span class="fin-num fin-stat-value">{{ formatMoney(grand.debit) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("totalCredit") }}</span>
          <span class="fin-num fin-stat-value">{{ formatMoney(grand.credit) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ t("entriesCount") }}</span>
          <span class="fin-num fin-stat-value">{{ grand.entries_count }}</span>
        </div>
        <v-chip small :color="grand.is_balanced ? 'green' : 'red'" text-color="white">
          <v-icon small class="me-1">{{ grand.is_balanced ? "check_circle" : "warning" }}</v-icon>
          {{ grand.is_balanced ? c("balanced") : c("notBalanced") }}
        </v-chip>
        <span class="gjs-period">{{ frPeriodText(report.header && report.header.period) }}</span>
      </div>

      <div v-if="hasReport && report.is_empty" class="gjs-empty">{{ report.message || c("noMovements") }}</div>

      <div v-for="section in sections" :key="section.currency" class="gjs-section">
        <div class="gjs-section-head">
          <span class="gjs-section-title">{{ $t("finance.reports2.generalJournalSummary.section", { title: section.title }) }}</span>
          <v-chip small :color="section.totals.is_balanced ? 'green' : 'red'" text-color="white">
            {{ section.totals.is_balanced ? c("balanced") : c("notBalanced") }}
          </v-chip>
          <span class="gjs-section-hint">{{ t("sectionHint") }}</span>
        </div>
        <div class="gjs-table-wrap fin-table-wrap">
          <table class="gjs-table fin-table">
            <thead>
              <tr>
                <th class="gjs-col-toggle fr-no-print"></th>
                <th class="gjs-col-code">{{ c("accountCode") }}</th>
                <th>{{ c("accountName") }}</th>
                <th v-if="detailed">{{ t("date") }}</th>
                <th v-if="detailed">{{ t("side") }}</th>
                <th class="fin-num-head">{{ t("entriesCount") }}</th>
                <th class="fin-num-head">{{ c("debit") }} ({{ section.currency }})</th>
                <th class="fin-num-head">{{ c("credit") }} ({{ section.currency }})</th>
                <th class="fin-num-head">{{ t("net") }}</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="item in sectionItems(section)">
                <tr :key="item.key" :class="item.classes">
                  <td class="gjs-col-toggle fr-no-print">
                    <v-btn
                      v-if="item.kind === 'account' && detailed && item.row.details && item.row.details.length"
                      icon
                      small
                      class="gjs-toggle"
                      :title="openDays[item.daysKey] ? t('hideDays') : t('showDays')"
                      @click="toggleDays(item.daysKey)"
                    >
                      <v-icon small>{{ openDays[item.daysKey] ? "expand_less" : "expand_more" }}</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      class="gjs-toggle"
                      :title="openDrills[item.drillKey] ? t('hideLines') : t('showLines')"
                      @click="toggleDrill(item.drillKey)"
                    >
                      <v-icon small>{{ openDrills[item.drillKey] ? "remove" : "add" }}</v-icon>
                    </v-btn>
                  </td>
                  <td class="gjs-col-code fin-code">{{ item.kind === "account" ? item.row.code : "" }}</td>
                  <td>
                    <template v-if="item.kind === 'account'">
                      <span class="fin-ellipsis" :title="item.row.name">{{ item.row.name }}</span>
                      <span v-if="item.row.rolled_up" class="gjs-rolled" :title="rolledHint(item.row)">
                        {{ t("rolledUp") }}
                      </span>
                    </template>
                  </td>
                  <td v-if="detailed" class="gjs-nowrap">{{ item.kind === "day" ? item.day.date : "" }}</td>
                  <td v-if="detailed">{{ item.kind === "day" ? sideLabel(item.day.side) : "" }}</td>
                  <td class="fin-num">{{ item.source.entries_count }}</td>
                  <td class="fin-num">{{ formatMoney(item.source.debit) }}</td>
                  <td class="fin-num">{{ formatMoney(item.source.credit) }}</td>
                  <td class="fin-num" :class="netClass(item.source.net)">{{ formatMoney(item.source.net) }}</td>
                </tr>
                <tr v-if="openDrills[item.drillKey]" :key="item.key + '-lines'" class="gjs-drill-row fr-no-print">
                  <td :colspan="columnCount">
                    <general-journal-summary-entries :params="item.drillParams" />
                  </td>
                </tr>
              </template>
              <tr v-if="!section.rows.length">
                <td :colspan="columnCount" class="fin-empty">{{ c("noMovements") }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="gjs-total-row" :class="{ 'gjs-total-bad': !section.totals.is_balanced }">
                <td class="fr-no-print"></td>
                <td :colspan="detailed ? 4 : 2">
                  {{ $t("finance.reports2.generalJournalSummary.sectionTotal", { currency: section.currency }) }}
                  <span class="gjs-total-check">
                    {{ section.totals.is_balanced ? c("balanced") + " ✓" : c("notBalanced") }}
                  </span>
                </td>
                <td class="fin-num">{{ section.totals.entries_count }}</td>
                <td class="fin-num">{{ formatMoney(section.totals.debit) }}</td>
                <td class="fin-num">{{ formatMoney(section.totals.credit) }}</td>
                <td class="fin-num">{{ formatMoney(section.totals.difference) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div v-if="showGrand" class="gjs-section">
        <div class="gjs-section-head">
          <span class="gjs-section-title">{{ t("grandTitle") }}</span>
        </div>
        <div class="gjs-table-wrap fin-table-wrap">
          <table class="gjs-table fin-table">
            <thead>
              <tr>
                <th></th>
                <th class="fin-num-head">{{ t("entriesCount") }}</th>
                <th class="fin-num-head">{{ c("debit") }} ({{ grand.currency }})</th>
                <th class="fin-num-head">{{ c("credit") }} ({{ grand.currency }})</th>
                <th class="fin-num-head">{{ t("difference") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, index) in grandLines" :key="'g' + index" :class="line.classes">
                <td>{{ line.label }}</td>
                <td class="fin-num">{{ line.entries_count }}</td>
                <td class="fin-num">{{ formatMoney(line.debit) }}</td>
                <td class="fin-num">{{ formatMoney(line.credit) }}</td>
                <td class="fin-num">{{ line.difference === null ? "" : formatMoney(line.difference) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="notes.length" class="gjs-notes">
        <div class="gjs-notes-title">{{ t("notes") }}</div>
        <ul>
          <li v-for="(note, index) in notes" :key="'n' + index">{{ note }}</li>
        </ul>
      </div>
    </v-card>
  </div>
</template>

<script>
/*
 * Report 2.1 of the accountant's document 2026-10-03 (general-journal-summary,
 * سجل اليومية العامة المجمع): posted movements per account in chart-tree
 * order, one section per currency (each balanced on its own), party leaves
 * rolled into their main account, a per-day detailed level, and a "+" drill
 * to the journal lines under any row.
 * API: this.api.ACTIONS.ACCOUNTING_GENERAL_JOURNAL_SUMMARY (GET, format=json|csv)
 *      this.api.ACTIONS.ACCOUNTING_GENERAL_JOURNAL_SUMMARY_ENTRIES (drill).
 * Money arrives in STORED units: formatMoney / the export mixin divide by 100.
 */
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import GeneralJournalSummaryEntries from "@/components/finance/GeneralJournalSummaryEntries";
import LoadingComponent from "@/components/LoadingComponent";
import financeReportExport from "@/mixins/financeReportExport";
import { companyToday } from "@/util/companyTime";

const SLUG = "general-journal-summary";

export default {
  name: "GeneralJournalSummary",
  title() {
    return this.$t("finance.reports2.generalJournalSummary.pageTitle");
  },
  components: { FinanceSectionNav, GeneralJournalSummaryEntries, LoadingComponent },
  mixins: [financeReportExport],
  data() {
    // Final-version doc 2026-10-05: the company (Damascus) day on the
    // server-synced clock, not the PC calendar.
    const today = companyToday();

    return {
      loading: false,
      report: null,
      openDrills: {},
      openDays: {},
      filters: {
        // Default period: the current month to date.
        from: today.slice(0, 8) + "01",
        to: today,
        currency: "all",
        level: "summarized",
        roll_up_parties: true,
      },
    };
  },
  computed: {
    hasReport() {
      return !!(this.report && this.report.sections);
    },
    sections() {
      return (this.report && this.report.sections) || [];
    },
    // The level of the LOADED report (the filter may have changed since).
    detailed() {
      return !!(this.report && this.report.filters && this.report.filters.level === "detailed");
    },
    columnCount() {
      return this.detailed ? 9 : 7;
    },
    grand() {
      return (this.report && this.report.grand_total) || {};
    },
    warnings() {
      return (this.report && this.report.warnings) || [];
    },
    notes() {
      return (this.report && this.report.notes) || [];
    },
    showGrand() {
      return this.hasReport && (this.sections.length > 1 || !!this.grand.has_foreign_currency);
    },
    grandLines() {
      const g = this.grand;
      const currency = g.currency || "SYP";
      const lines = [
        {
          label: this.$t("finance.reports2.generalJournalSummary.grandTotal", { currency: currency }),
          entries_count: g.entries_count,
          debit: g.debit,
          credit: g.credit,
          difference: g.difference_before_fx,
          classes: { "gjs-total-row": !g.fx_difference },
        },
      ];
      if (g.fx_difference) {
        lines.push({
          label: this.t("fxLine"),
          entries_count: "",
          debit: g.fx_debit,
          credit: g.fx_credit,
          difference: null,
          classes: { "gjs-fx-row": true },
        });
        lines.push({
          label: this.$t("finance.reports2.generalJournalSummary.grandAfterFx", { currency: currency }),
          entries_count: g.entries_count,
          debit: Number(g.debit || 0) + Number(g.fx_debit || 0),
          credit: Number(g.credit || 0) + Number(g.fx_credit || 0),
          difference: g.difference,
          classes: { "gjs-total-row": true },
        });
      }
      return lines;
    },
    currencyOptions() {
      const codes = ["SYP", "USD"];
      this.sections.forEach((section) => {
        if (codes.indexOf(section.currency) === -1) {
          codes.push(section.currency);
        }
      });
      return [{ text: this.t("allCurrencies"), value: "all" }].concat(
        codes.map((code) => ({ text: code, value: code }))
      );
    },
    levelOptions() {
      return [
        { text: this.t("levelSummarized"), value: "summarized" },
        { text: this.t("levelDetailed"), value: "detailed" },
      ];
    },
  },
  mounted() {
    this.loadReport();
  },
  methods: {
    t(key) {
      return this.$t("finance.reports2.generalJournalSummary." + key);
    },
    c(key) {
      return this.$t("finance.reports2.common." + key);
    },
    buildParams() {
      return {
        from: this.filters.from,
        to: this.filters.to,
        currency: this.filters.currency || "all",
        level: this.filters.level || "summarized",
        roll_up_parties: this.filters.roll_up_parties ? 1 : 0,
      };
    },
    validPeriod() {
      if (!this.filters.from || !this.filters.to) {
        window.getApp.$emit("SHOW_TOAST", this.t("periodRequired"), "red");
        return false;
      }
      return true;
    },
    loadReport() {
      if (!this.validPeriod()) {
        return;
      }
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_GENERAL_JOURNAL_SUMMARY, { params: this.buildParams() })
        .then((response) => {
          this.report = response.data.data || null;
          this.openDrills = {};
          this.openDays = {};
        })
        .catch((error) => {
          window.getApp.$emit("SHOW_TOAST", this.frErrorMessage(error, this.c("failedLoad")), "red");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    downloadCsv() {
      if (!this.validPeriod()) {
        return;
      }
      this.loading = true;
      this.frDownloadCsv(this.api.ACTIONS.ACCOUNTING_GENERAL_JOURNAL_SUMMARY, this.buildParams(), SLUG).finally(() => {
        this.loading = false;
      });
    },
    daysKey(section, row) {
      return section.currency + "|" + row.account_id;
    },
    drillParams(section, row, day) {
      const filters = this.report.filters || {};
      return {
        account_id: row.account_id,
        from: filters.from,
        to: filters.to,
        currency: section.currency,
        date: day ? day.date : null,
        side: day ? day.side : null,
        roll_up_parties: filters.roll_up_parties ? 1 : 0,
      };
    },
    /** Rows of one section as rendered: account rows, then their open day rows. */
    sectionItems(section) {
      const items = [];
      section.rows.forEach((row) => {
        const daysKey = this.daysKey(section, row);
        const drillKey = daysKey + "||";
        items.push({
          kind: "account",
          key: "a|" + drillKey,
          row: row,
          source: row,
          daysKey: daysKey,
          drillKey: drillKey,
          drillParams: this.drillParams(section, row, null),
          classes: { "gjs-account-row": true, "gjs-rolled-row": row.rolled_up, "gjs-bold": this.detailed },
        });
        if (this.detailed && this.openDays[daysKey]) {
          (row.details || []).forEach((day) => {
            const dayDrillKey = daysKey + "|" + day.date + "|" + day.side;
            items.push({
              kind: "day",
              key: "d|" + dayDrillKey,
              row: row,
              day: day,
              source: day,
              daysKey: daysKey,
              drillKey: dayDrillKey,
              drillParams: this.drillParams(section, row, day),
              classes: { "gjs-day-row": true },
            });
          });
        }
      });
      return items;
    },
    toggleDays(key) {
      const next = Object.assign({}, this.openDays);
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      this.openDays = next;
    },
    toggleDrill(key) {
      const next = Object.assign({}, this.openDrills);
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      this.openDrills = next;
    },
    expandAll() {
      if (!this.detailed) {
        return;
      }
      const next = {};
      this.sections.forEach((section) => {
        section.rows.forEach((row) => {
          if (row.details && row.details.length) {
            next[this.daysKey(section, row)] = true;
          }
        });
      });
      this.openDays = next;
    },
    collapseAll() {
      this.openDays = {};
      this.openDrills = {};
    },
    sideLabel(side) {
      return side === "credit" ? this.t("sideCredit") : this.t("sideDebit");
    },
    rolledHint(row) {
      return this.$t("finance.reports2.generalJournalSummary.rolledUpHint", { count: row.accounts_count });
    },
    netClass(value) {
      const n = Number(value || 0);
      if (n > 0) return "gjs-net-debit";
      if (n < 0) return "gjs-net-credit";
      return "";
    },

    // ----- exports (the whole loaded report, every day row, no drill lines) -----

    exportColumns() {
      const columns = [
        { key: "code", label: this.c("accountCode"), type: "code", width: 18 },
        { key: "name", label: this.c("accountName"), type: "text", width: 40, indent: true },
      ];
      if (this.detailed) {
        columns.push({ key: "date", label: this.t("date"), type: "date", width: 12 });
        columns.push({ key: "side", label: this.t("side"), type: "text", width: 8 });
      }
      return columns.concat([
        { key: "entries_count", label: this.t("entriesCount"), type: "count", width: 10 },
        { key: "debit", label: this.c("debit"), type: "money" },
        { key: "credit", label: this.c("credit"), type: "money" },
        { key: "net", label: this.t("net"), type: "money" },
      ]);
    },
    exportRows(section) {
      const rows = [];
      section.rows.forEach((row) => {
        rows.push({
          code: row.code,
          name: row.name + (row.rolled_up ? " (" + this.t("rolledUp") + ")" : ""),
          entries_count: row.entries_count,
          debit: row.debit,
          credit: row.credit,
          net: row.net,
          _level: 0,
          _bold: this.detailed,
        });
        if (this.detailed) {
          (row.details || []).forEach((day) => {
            rows.push({
              code: "",
              name: "",
              date: day.date,
              side: this.sideLabel(day.side),
              entries_count: day.entries_count,
              debit: day.debit,
              credit: day.credit,
              net: day.net,
              _level: 1,
            });
          });
        }
      });
      const totals = section.totals;
      rows.push({
        code: "",
        name:
          this.$t("finance.reports2.generalJournalSummary.sectionTotal", { currency: section.currency }) +
          " - " +
          (totals.is_balanced ? this.c("balanced") : this.c("notBalanced")),
        entries_count: totals.entries_count,
        debit: totals.debit,
        credit: totals.credit,
        net: totals.difference,
        _kind: "total",
        _highlight: totals.is_balanced ? null : "error",
      });
      return rows;
    },
    grandExport() {
      const columns = [
        { key: "label", label: this.t("grandTitle"), type: "text", width: 44 },
        { key: "entries_count", label: this.t("entriesCount"), type: "count", width: 10 },
        { key: "debit", label: this.c("debit"), type: "money" },
        { key: "credit", label: this.c("credit"), type: "money" },
        { key: "difference", label: this.t("difference"), type: "money" },
      ];
      const rows = this.grandLines.map((line) => ({
        label: line.label,
        entries_count: line.entries_count,
        debit: line.debit,
        credit: line.credit,
        difference: line.difference,
        _kind: line.classes["gjs-fx-row"] ? "detail" : "total",
        _highlight: line.classes["gjs-fx-row"] ? "warn" : null,
      }));
      return { columns, rows };
    },
    exportNotes() {
      return this.warnings.map((warning) => warning.text).concat(this.notes);
    },
    sectionTitle(section) {
      return this.$t("finance.reports2.generalJournalSummary.section", { title: section.title });
    },
    exportExcel() {
      if (!this.hasReport) {
        return;
      }
      const columns = this.exportColumns();
      const sheets = this.sections.map((section) => ({
        name: section.currency,
        title: this.sectionTitle(section),
        columns: columns,
        rows: this.exportRows(section),
        notes: this.showGrand ? [] : this.exportNotes(),
      }));
      if (this.showGrand) {
        const grand = this.grandExport();
        sheets.push({
          name: this.t("grandSheet"),
          title: this.t("grandTitle"),
          columns: grand.columns,
          rows: grand.rows,
          notes: this.exportNotes(),
        });
      }
      this.frExportXlsx({ fileName: SLUG, header: this.report.header, sheets: sheets });
    },
    exportPrint() {
      if (!this.hasReport) {
        return;
      }
      const columns = this.exportColumns();
      const sections = this.sections.map((section) => ({
        title: this.sectionTitle(section),
        columns: columns,
        rows: this.exportRows(section),
      }));
      if (this.showGrand) {
        const grand = this.grandExport();
        sections.push({ title: this.t("grandTitle"), columns: grand.columns, rows: grand.rows });
      }
      this.frPrint({
        header: this.report.header,
        sections: sections,
        notes: this.exportNotes(),
        orientation: this.detailed ? "landscape" : "portrait",
      });
    },
  },
};
</script>

<style scoped>
.gjs-section {
  padding: 8px 8px 4px;
}
.gjs-section-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 4px;
}
.gjs-section-title {
  font-weight: 700;
  font-size: 15px;
}
.gjs-section-hint {
  font-size: 12px;
  font-style: italic;
  color: rgba(0, 0, 0, 0.55);
}
/* Table chrome comes from the shared .fin-table layer (finance-compact.css,
   final-version doc 2026-10-05). */
/* A section can be long (one row per account): its own scroll box with a
   sticky header; short sections shrink to their content. */
.gjs-table-wrap {
  min-height: 0;
  margin: 0;
}
.gjs-col-toggle {
  width: 64px;
  padding: 0 2px !important;
}
.gjs-col-code {
  width: 150px;
}
.gjs-toggle {
  margin: 0;
  height: 22px;
  width: 22px;
}
.gjs-bold td {
  font-weight: 600;
}
.gjs-rolled-row td {
  background: #f5f7fa;
}
.gjs-rolled {
  display: inline-block;
  margin-inline-start: 6px;
  padding: 0 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 400;
  background: #e3f2fd;
  color: #1565c0;
}
.gjs-day-row td {
  color: rgba(0, 0, 0, 0.75);
  font-size: 12px;
}
.gjs-drill-row > td {
  padding: 0 !important;
  white-space: normal;
}
.gjs-total-row td {
  font-weight: 700;
  background: #e8f5e9;
}
.gjs-total-bad td {
  background: #ffcdd2;
}
.gjs-fx-row td {
  background: #fff3e0;
}
.gjs-total-check {
  margin-inline-start: 8px;
  font-weight: 400;
}
.gjs-ltr {
  direction: ltr;
  unicode-bidi: embed;
}
.gjs-nowrap {
  white-space: nowrap;
}
.gjs-net-debit {
  color: #1565c0;
}
.gjs-net-credit {
  color: #2e7d32;
}
.gjs-empty {
  text-align: center;
  padding: 20px;
  color: rgba(0, 0, 0, 0.55);
}
.gjs-period {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}
.gjs-checkbox {
  margin-top: 0;
  padding-top: 0;
}
.gjs-notes {
  padding: 8px 16px 16px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
}
.gjs-notes-title {
  font-weight: 700;
  margin-bottom: 2px;
}
.gjs-notes ul {
  margin: 0;
  padding-inline-start: 18px;
}
@media print {
  .fr-no-print {
    display: none !important;
  }
}
</style>
